/**
 * Move repositories onto Teams: the one-time sheet that dissolves the Reviews
 * team. Every repository the catch-all still reviews gets a select of Teams,
 * defaulting to the Team whose builds open its pull requests
 * (`suggestedDeskId`), with "Keep on the Reviews team" last. One
 * `POST /api/code-review/repo-claims/move` carries every choice that names a
 * Team; a move that fails is printed on its own row and the rest still land.
 * When nothing is left the sheet says the Reviews team has dissolved.
 */
import { useMemo, useState } from "react";
import type { RepoClaimMoved, RepoClaimRow } from "@yagni/shared";
import { Pill, RecordHead, SheetFoot } from "../queue/index.js";
import type { ReviewsApi } from "./reviewsApi.js";
import { KEEP_ON_REVIEWS_TEAM, MOVE_FAILURE_COPY, planMoves } from "./reviewsModel.js";

export interface MoveReposSheetProps {
  api: ReviewsApi;
  /** The repositories still on the Reviews team. */
  unowned: readonly RepoClaimRow[];
  /** The Teams a repository can move onto (every readable Team but the Reviews team). */
  teams: readonly { id: string; name: string }[];
  /** A move landed: the page re-reads who reviews what. */
  onMoved: () => void;
  onCancel: () => void;
}

export function MoveReposSheet({ api, unowned, teams, onMoved, onCancel }: MoveReposSheetProps) {
  const teamIds = useMemo(() => new Set(teams.map((team) => team.id)), [teams]);
  const [choices, setChoices] = useState<Map<string, string>>(
    () =>
      new Map(
        unowned.map((claim) => [
          claim.repo,
          claim.suggestedDeskId && teamIds.has(claim.suggestedDeskId) ? claim.suggestedDeskId : KEEP_ON_REVIEWS_TEAM,
        ]),
      ),
  );
  const [moved, setMoved] = useState<RepoClaimMoved[]>([]);
  const [failed, setFailed] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const movedRepos = new Set(moved.map((entry) => entry.repo.toLowerCase()));
  const left = unowned.filter((claim) => !movedRepos.has(claim.repo.toLowerCase()));
  const leftChoices = new Map(left.map((claim) => [claim.repo, choices.get(claim.repo) ?? KEEP_ON_REVIEWS_TEAM]));
  const moves = planMoves(leftChoices);

  const submit = async () => {
    if (moves.length === 0) return;
    setBusy(true);
    setError(null);
    const result = await api.moveRepoClaims(moves);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setMoved((was) => [...was, ...result.value.moved]);
    setFailed(new Map(result.value.failed.map((entry) => [entry.repo.toLowerCase(), entry.reason])));
    if (result.value.moved.length > 0) onMoved();
  };

  if (left.length === 0) {
    const byTeam = new Map<string, number>();
    for (const entry of moved) if (entry.toDeskId) byTeam.set(entry.toDeskId, (byTeam.get(entry.toDeskId) ?? 0) + 1);
    return (
      <>
        <RecordHead recordKey="MOVED" title="Every repository is owned by a Team" noExpand onClose={onCancel} meta={<Pill kind="live">Done</Pill>} />
        <div className="sbody" data-testid="move-repos-done">
          <p className="brief">
            The Reviews team has dissolved. Each Team reviews its own repositories at its own level, with its own checks and its own On GitHub setting.
          </p>
          {byTeam.size > 0 && (
            <div className="blk">
              <div className="bh">
                <span className="label">What moved</span>
              </div>
              <div className="checks">
                {Array.from(byTeam.entries()).map(([deskId, count]) => (
                  <div key={deskId}>
                    <span className="ok">{count}</span>
                    <span>to {teams.find((team) => team.id === deskId)?.name ?? "a Team"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <SheetFoot
          why={
            <>
              Each Team's review line now lists its repositories. <b>Tune the level there.</b>
            </>
          }
          primary={{ label: "Next item", onClick: onCancel }}
        />
      </>
    );
  }

  return (
    <>
      <RecordHead
        recordKey="ONE-TIME MOVE"
        title="Move repositories onto Teams"
        noExpand
        onClose={onCancel}
        meta={
          <>
            <Pill kind="needs">{left.length} unowned</Pill>
            <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
              suggested by whose builds open its pull requests
            </span>
          </>
        }
      />
      <div className="sbody" data-testid="move-repos-sheet">
        <p className="body-p" style={{ color: "var(--text-primary)" }}>
          The Reviews team reviews these for everyone, so nobody can tune Proctor for them. Pick the Team that owns each one. Its review level, checks and On GitHub setting copy onto that Team, and the Team can change them.
        </p>
        <div className="blk">
          <div className="bh">
            <span className="label">Repositories</span>
            <span className="fact">{left.length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {left.map((claim) => {
              const reason = failed.get(claim.repo.toLowerCase());
              return (
                <div
                  key={claim.repo}
                  style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "center", padding: "6px 8px" }}
                  data-testid={`move-row-${claim.repo}`}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ font: "400 12.5px/1.4 var(--mono)", color: "var(--text-primary)", overflowWrap: "anywhere" }}>{claim.repo}</span>
                    {reason && (
                      <span className="fact" role="alert" style={{ display: "block", fontSize: 11, color: "var(--honesty)" }}>
                        {MOVE_FAILURE_COPY[reason] ?? reason}
                      </span>
                    )}
                  </span>
                  <span className="field" style={{ display: "block" }}>
                    <select
                      aria-label={`Team for ${claim.repo}`}
                      value={leftChoices.get(claim.repo)}
                      onChange={(event) => {
                        const value = event.target.value;
                        setChoices((was) => new Map(was).set(claim.repo, value));
                      }}
                    >
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                      <option value={KEEP_ON_REVIEWS_TEAM}>Keep on the Reviews team</option>
                    </select>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {error && (
          <p className="body-p" role="alert">
            {error}
          </p>
        )}
      </div>
      <SheetFoot
        why={
          <>
            Moving copies the level, checks and On GitHub setting onto each Team. <b>The Reviews team dissolves when it is empty.</b>
          </>
        }
        secondary={{ label: "Cancel", onClick: onCancel }}
        primary={{
          label: `Move ${moves.length} ${moves.length === 1 ? "repository" : "repositories"}`,
          onClick: () => void submit(),
          disabled: busy || moves.length === 0,
        }}
      />
    </>
  );
}
